import { PlusIcon } from '@heroicons/react/24/outline';
import React, { type FC } from 'react';

import { CircularLoadingAnimation } from '@/common/circular-loading-animation';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { GridIcon } from '@/common/grid-icon';
import { IntaglioIcon } from '@/common/intaglio-icon';
import { ListIcon } from '@/common/list-icon';
import { Navbar, NavbarType } from '@/common/navbar';
import { Skeleton } from '@/common/skeleton';
import { UploadIcon } from '@/common/upload-icon';

import { useTemplateEditorStore } from './store';

export const LoadingScreen: FC = () => {
    const fieldsPanelWidth = useTemplateEditorStore((s) => s.fieldsPanelWidth);

    return (
        <div className="flex flex-col min-h-0 h-screen w-full bg-black-50">
            <Navbar
                type={NavbarType.LIGHT}
                leading={
                    <div className="flex flex-row gap-4 items-center">
                        <IntaglioIcon />
                        <Skeleton className="w-32 h-5" />
                        <div className="flex flex-row gap-0.5">
                            <div className="py-1 px-2 rounded-md bg-gray-100">
                                <ListIcon />
                            </div>
                            <div className="py-1 px-2 rounded-md">
                                <GridIcon />
                            </div>
                        </div>
                    </div>
                }
                center={<div />}
                trailing={<div className="bg-blue-60 h-9 w-28 rounded" />}
            />

            <div className="flex min-h-0 flex-row h-full max-w-full min-w-full">
                <FloatingPanel
                    className="pl-4 py-4 h-full w-64 flex-shrink-0 flex-grow-0"
                    type={FloatingPanelType.LIGHT}
                >
                    <FloatingPanelItem className="px-4">
                        <p className="text-white text-base">Untitled Template</p>
                        <Skeleton className="w-40 h-5 mt-1" />
                    </FloatingPanelItem>

                    <FloatingPanelItem className="px-4">
                        <div className="bg-blue-60 h-11 w-full mt-4 rounded flex items-center justify-center gap-2">
                            <UploadIcon className="h-5 w-5 stroke-white" />
                            <Skeleton className="bg-white opacity-70 w-20 h-3" />
                        </div>
                    </FloatingPanelItem>

                    <FloatingPanelItem className=" px-4 flex flex-col gap-2 h-full max-h-full min-h-0">
                        <Skeleton className="w-20 h-5 mt-4" />
                        <div className="w-full bg-black-50 h-40 rounded " />
                        <div className="flex flex-row items-center justify-between">
                            <Skeleton className="w-24 h-5" />
                            <p className="text-gray-90 text-xs">1024 x 1024</p>
                        </div>
                    </FloatingPanelItem>
                </FloatingPanel>

                <div className="w-full h-full flex flex-col items-center justify-center">
                    <CircularLoadingAnimation className="w-16 h-16" />
                    <p className="text-gray-60 text-2xl">Setting up your template...</p>
                </div>

                <FloatingPanel
                    className="pr-4 py-4 h-full w-80 flex-shrink-0 flex-grow-0"
                    type={FloatingPanelType.LIGHT}
                    width={fieldsPanelWidth}
                >
                    <FloatingPanelItem className="px-4">
                        <div className="flex flex-row items-center justify-between">
                            <Skeleton className="w-32 h-5" />
                            <PlusIcon className="h-5 w-5 text-white" />
                        </div>
                    </FloatingPanelItem>

                    <FloatingPanelItem className="flex flex-col items-center justify-center gap-2 h-full max-h-full min-h-0">
                        <Skeleton className="w-[80%] h-5" />
                        <Skeleton className="w-[60%] h-5" />
                    </FloatingPanelItem>
                </FloatingPanel>
            </div>
        </div>
    );
};
